import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LRUCache } from "lru-cache";
import api from "utils/api";
import { useRequest } from "hooks/useRequest";
import { useLatest } from "hooks/useLatest";
import { useSearchParamState } from "hooks/useSearchParamState";
import { fetchSingleFloorPlan } from "../../utils/fetchSingleFloorPlan";
import { SIGNED_URL_SAFE_TTL_MS } from "../../utils/signedAssetUrls";
import {
  findFirstActivePlanIndex,
  findFirstArchivedPlanIndex,
  resolvePlanRecordIndex,
} from "./utils";
import type { FloorPlanRecord } from "components/SmartSurveyor/SmartSurveyorForRudolfFritz/hooks/types";
import type { PlanFolderPlacement, PlanV3 } from "../../utils/types";
import type {
  PatchCachedPlanResult,
  UseLazyPlanLoaderArgs,
  UseLazyPlanLoaderResult,
} from "./types";

export const useLazyPlanLoader = ({
  projectId,
}: UseLazyPlanLoaderArgs): UseLazyPlanLoaderResult => {
  const [floorPlanIdParam, setFloorPlanIdParam] = useSearchParamState<
    number | null
  >({
    key: "floorPlanId",
    defaultValue: null,
    parse: Number,
    serialize: (value) => (value === null ? "" : String(value)),
    replace: true,
  });

  const [selectedPlan, setSelectedPlan] = useState<PlanV3 | null>(null);

  const [pendingPlanRecordIndex, setPendingPlanRecordIndex] = useState<
    number | null
  >(null);

  // TTL: a plan's signed asset URLs expire, so a stale entry is a cache miss.
  const planCacheRef = useRef(
    new LRUCache<number, PlanV3>({ max: 50, ttl: SIGNED_URL_SAFE_TTL_MS }),
  );
  const requestCounterRef = useRef(0);
  const floorPlanIdParamRef = useLatest(floorPlanIdParam);

  const {
    data: planRecords = [],
    isLoading: isInitialLoading,
    mutateData: mutatePlanRecords,
  } = useRequest<FloorPlanRecord[], []>(
    async () => {
      const [activeRecords, archivedRecords] = await Promise.all([
        api.getQuantitySurveyorFloorPlansByProject(projectId),
        api.getQuantitySurveyorFloorPlansByProject(projectId, {
          archived: true,
        }),
      ]);
      const records = [...activeRecords, ...archivedRecords];

      const matchedIndex = resolvePlanRecordIndex(
        records,
        floorPlanIdParamRef.current,
      );
      // Load-time default; archived plans are auto-opened only when no active plan exists.
      const initialIndex =
        matchedIndex ??
        findFirstActivePlanIndex(records) ??
        findFirstArchivedPlanIndex(records);

      if (initialIndex !== null) {
        const record = records[initialIndex];
        try {
          const initialPlan = await fetchSingleFloorPlan({
            record,
            index: initialIndex,
          });
          planCacheRef.current.set(record.id, initialPlan);
          setSelectedPlan(initialPlan);
        } catch {
          // Still expose the records; the effect below retries on the cache miss.
        }
        // Written after the fetch so the URL update and the records land in one commit.
        if (matchedIndex === null) setFloorPlanIdParam(record.id);
      }

      // Returned last: `data` is exposed only after the cache is warmed, so the
      // effect below hits the cache instead of refetching.
      return records;
    },
    { manual: false, defaultParams: [] },
  );

  const selectedPlanRecordIndex = useMemo(
    () => resolvePlanRecordIndex(planRecords, floorPlanIdParam),
    [planRecords, floorPlanIdParam],
  );

  const selectPlanRecord = useCallback(
    (recordIndex: number) => {
      const record = planRecords[recordIndex];
      if (!record) return;
      setFloorPlanIdParam(record.id);
    },
    [planRecords, setFloorPlanIdParam],
  );

  const clearPlanSelection = useCallback(() => {
    setFloorPlanIdParam(null);
  }, [setFloorPlanIdParam]);

  const updateCachedPlans = useCallback(
    (floorPlanIds: number[], updater: (plan: PlanV3) => PlanV3) => {
      const cache = planCacheRef.current;

      floorPlanIds.forEach((floorPlanId) => {
        const cached = cache.get(floorPlanId);
        if (!cached) return;
        // The URLs inside are unchanged, so the entry keeps its original TTL.
        cache.set(floorPlanId, updater(cached), { noUpdateTTL: true });
      });

      // A TTL-expired entry misses the cache write above, but the edit must
      // still reach the plan on screen.
      setSelectedPlan((prev) => {
        if (!prev || !floorPlanIds.includes(prev.floorPlanId)) return prev;
        return cache.get(prev.floorPlanId) ?? updater(prev);
      });
    },
    [],
  );

  const updateCachedPlan = useCallback(
    (floorPlanId: number, updater: (plan: PlanV3) => PlanV3) => {
      updateCachedPlans([floorPlanId], updater);
    },
    [updateCachedPlans],
  );

  const reloadCachedPlan = useCallback(
    async (floorPlanId: number) => {
      const index = planRecords.findIndex(
        (record) => record.id === floorPlanId,
      );
      if (index === -1) return;

      // Leaves the selection counter alone, so a plan being opened meanwhile still finishes loading.
      try {
        const plan = await fetchSingleFloorPlan({
          record: planRecords[index],
          index,
        });
        planCacheRef.current.set(floorPlanId, plan);
        setSelectedPlan((prev) =>
          prev?.floorPlanId === floorPlanId ? plan : prev,
        );
      } catch {
        // The stale plan stays visible; the next selection refetches it.
      }
    },
    [planRecords],
  );

  const patchCachedPlan = useCallback(
    (floorPlanId: number, patcher: (plan: PlanV3) => PatchCachedPlanResult) => {
      const current =
        planCacheRef.current.get(floorPlanId) ??
        (selectedPlan?.floorPlanId === floorPlanId ? selectedPlan : null);
      if (current === null) return;

      const result = patcher(current);
      if (result.kind === "reload") {
        reloadCachedPlan(floorPlanId);
        return;
      }
      updateCachedPlan(floorPlanId, () => result.plan);
    },
    [reloadCachedPlan, updateCachedPlan, selectedPlan],
  );

  const updatePlanRecordName = useCallback(
    (recordIndex: number, newName: string) => {
      const record = planRecords[recordIndex];
      if (!record) return;

      mutatePlanRecords({
        updater: (records) =>
          records.map((item, index) =>
            index === recordIndex ? { ...item, name: newName } : item,
          ),
      });

      updateCachedPlan(record.id, (plan) => ({ ...plan, label: newName }));
    },
    [planRecords, mutatePlanRecords, updateCachedPlan],
  );

  // Pure record mutation; the sidebar owns selecting a successor afterwards.
  const updatePlanRecordArchivedAt = useCallback(
    (recordIndex: number, archivedAt: string | null) => {
      mutatePlanRecords({
        updater: (records) =>
          records.map((item, index) =>
            index === recordIndex ? { ...item, archivedAt } : item,
          ),
      });
    },
    [mutatePlanRecords],
  );

  const updatePlanRecordFolderId = useCallback(
    (floorPlanId: number, folderId: number | null) => {
      mutatePlanRecords({
        updater: (records) =>
          records.map((item) =>
            item.id === floorPlanId ? { ...item, folderId } : item,
          ),
      });
    },
    [mutatePlanRecords],
  );

  const setPlanRecordFolderIds = useCallback(
    (planFolders: PlanFolderPlacement[]) => {
      const folderIdByPlanId = new Map(
        planFolders.map(({ floorPlanId, folderId }) => [floorPlanId, folderId]),
      );
      mutatePlanRecords({
        updater: (records) =>
          records.map((item) => {
            const folderId = folderIdByPlanId.get(item.id);
            return folderId === undefined ? item : { ...item, folderId };
          }),
      });
    },
    [mutatePlanRecords],
  );

  // Mirrors the DB's ON DELETE SET NULL, so a deleted folder's plans return to the root without a refetch.
  const detachPlanRecordsFromFolder = useCallback(
    (folderId: number) => {
      mutatePlanRecords({
        updater: (records) =>
          records.map((item) =>
            item.folderId === folderId ? { ...item, folderId: null } : item,
          ),
      });
    },
    [mutatePlanRecords],
  );

  // Pure record + cache eviction; the sidebar owns selecting a successor afterwards.
  const removePlanRecord = useCallback(
    (recordIndex: number) => {
      const record = planRecords[recordIndex];
      if (!record) return;

      planCacheRef.current.delete(record.id);
      mutatePlanRecords({
        updater: (records) =>
          records.filter((item, index) => index !== recordIndex),
      });
    },
    [planRecords, mutatePlanRecords],
  );

  useEffect(() => {
    if (selectedPlanRecordIndex === null) {
      // Nothing selected -> drop the previously shown plan from the viewer.
      setSelectedPlan(null);
      setPendingPlanRecordIndex(null);
      return;
    }
    const record = planRecords[selectedPlanRecordIndex];

    // Invalidate any in-flight fetch so a slower earlier selection can't
    // overwrite this one (e.g. selecting a cached plan mid-load).
    const requestId = ++requestCounterRef.current;

    const cached = planCacheRef.current.get(record.id);
    if (cached) {
      setSelectedPlan(cached);
      setPendingPlanRecordIndex(null);
      return;
    }

    setPendingPlanRecordIndex(selectedPlanRecordIndex);

    (async () => {
      try {
        const plan = await fetchSingleFloorPlan({
          record,
          index: selectedPlanRecordIndex,
        });
        if (requestId !== requestCounterRef.current) return;
        planCacheRef.current.set(record.id, plan);
        setSelectedPlan(plan);
      } catch {
        // Keep the previous plan visible on error
      } finally {
        if (requestId === requestCounterRef.current) {
          setPendingPlanRecordIndex(null);
        }
      }
    })();
  }, [planRecords, selectedPlanRecordIndex]);

  return {
    isInitialLoading,
    planRecords,
    selectedPlanRecordIndex,
    pendingPlanRecordIndex,
    selectedPlan,
    selectPlanRecord,
    updateCachedPlan,
    updateCachedPlans,
    patchCachedPlan,
    reloadCachedPlan,
    updatePlanRecordName,
    updatePlanRecordArchivedAt,
    updatePlanRecordFolderId,
    setPlanRecordFolderIds,
    detachPlanRecordsFromFolder,
    removePlanRecord,
    clearPlanSelection,
  };
};
